import { useState, useEffect } from 'react';
import { Box, Stack, Button, Paper, CircularProgress, Autocomplete, TextField, } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import { search, save } from 'store/application/application-lottery'
import { useDispatch, useSelector } from 'react-redux';
import * as snackbar from 'utils/snackbar'
import { useTranslation } from 'react-i18next';
import NoRecords from 'components/NoRecords';
import client from 'utils/api';

const LotteryFiles = ({ clientSchools }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const list = useSelector(state => state.application?.applicationLottery?.list);
    const listLoading = useSelector(state => state.application?.applicationLottery?.loading);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const selectedSchoolName = selectedSchool?.abbreviation || '';

    const columns = [
        { field: 'applicationNo', headerName: 'Application No', flex: 1 },
        { field: 'applicant', headerName: 'Applicant', flex: 1, renderCell: (params) => params.value },
        { field: 'grade', headerName: 'Grade', flex: 0.5 },
        { field: 'city', headerName: 'City', flex: 1 },
        selectedSchool && selectedSchool.abbreviation === 'Bronx' &&
        { field: 'csdistrict', headerName: 'Resident District', flex: 1 },
        { field: 'ell', headerName: 'ELL', flex: 0.5, renderCell: (params) => params.value ? 'Yes' : 'No' },

        ...(selectedSchool ? [
            selectedSchool.abbreviation === 'Bronx'
                ? {
                    field: 'swd',
                    headerName: 'SWD',
                    flex: 0.5,
                    renderCell: (params) => params.value ? 'Yes' : 'No'
                }
                : {
                    field: 'edc',
                    headerName: 'EDC',
                    flex: 0.5,
                    renderCell: (params) => params.value ? 'Yes' : 'No'
                }
        ] : [])
    ].filter(Boolean);


    const handleUploadSubmit = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv,text/csv,application/vnd.ms-excel';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const validTypes = [
                'text/csv',
                'application/vnd.ms-excel',
                'application/csv',
                'text/plain'
            ];
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!validTypes.includes(file.type) && fileExtension !== 'csv') {
                snackbar.error(t('Only CSV files are allowed.'));
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                snackbar.error(t('File size exceeds the 10MB limit. Please upload a smaller file.'));
                return;
            }
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('afile', file, file.name);

                const uploadRes = await fetch('/api/file', {
                    method: 'POST',
                    body: formData,
                });
                const uploadData = await uploadRes.json();
                const fileId = uploadData.uuid;
                const response = await client.get(`/api/file/${fileId}/url`);
                const fileURL = response?.data || response;

                const result = await dispatch(save({ fileURL }));
                if (result?.error) {
                    throw new Error(result.error.message || 'Save failed');
                }
                snackbar.success('File uploaded successfully');
            } catch (error) {
                console.error('File upload failed:', error);
                snackbar.error('File upload failed');
            } finally {
                setIsUploading(false);
            }
        };
        input.click();
    };

    useEffect(() => {
        if (!selectedSchool) return;
        try {
            dispatch(search({ schoolId: selectedSchool.id }))

        } catch (error) {
            console.error('Error fetching lottery data:', error);
            snackbar.error('Failed to fetch lottery data');
        }

    }, [selectedSchool, dispatch]);

    const reOrganizedList = [];
    if (list && list.length > 0) {
        let index = 0;
        list.forEach(gradeData => {
            const grade = gradeData.grade;
            const items = gradeData.items || [];
            items.forEach(item => {
                reOrganizedList.push({ ...item, grade, uniqueId: index++ });
            });
        });
    }

    const handleDownloadAllTxt = async () => {
        if (!list || list.length === 0) return;

        const downloadFile = (grade, content) => {
            return new Promise((resolve) => {
                const blob = new Blob([content], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedSchoolName}_Grade_${grade}.txt`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                setTimeout(resolve, 300);
            });
        };

        for (const gradeData of list) {
            const grade = gradeData.grade;
            const records = gradeData.items || [];

            if (records.length === 0) continue;

            const rows = records.map(r =>
                `${r.applicationNo}\t${r.applicant}`
            ).join("\n");

            await downloadFile(grade, rows);
        }

        snackbar.success('All files downloaded');
    };

    return (
        <Box>
            <Paper sx={{ p: 2, gap: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }} elevation={0}>
                <Box width={'30%'}>
                    <Autocomplete
                        options={clientSchools}
                        getOptionLabel={option => option?.abbreviation || ''}
                        onChange={(_, value) => {
                            setSelectedSchool(value);
                        }}
                        sx={{ width: '100%' }}
                        renderInput={(params) => (
                            <TextField {...params} label="Select School" variant="outlined" size="small" fullWidth />
                        )}
                    />
                </Box>
                <Stack direction="row" spacing={2} width={'70%'} justifyContent="flex-end">
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadAllTxt}
                        disabled={!list || list.length === 0}
                    >
                        {t('Download All .txt')}
                    </Button>
                    <Button variant="outlined" onClick={() => handleUploadSubmit()} disabled={isUploading}>
                        {t('Upload Lottery CSV')}
                        {isUploading && (
                            <CircularProgress size={20} sx={{ ml: 1 }} />
                        )}
                    </Button>
                </Stack>
            </Paper>
            <Paper elevation={0} sx={{ p: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <Stack spacing={3}>
                    {listLoading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : reOrganizedList.length > 0 && selectedSchool ? (
                        <DataGrid
                            rows={reOrganizedList}
                            columns={columns}
                            getRowId={(row) => row.uniqueId}
                            autoHeight
                            disableRowSelectionOnClick
                            pageSizeOptions={[5, 10, 25, 50]}
                            sx={{
                                '& .MuiDataGrid-columnHeaders': {
                                    bgcolor: 'action.hover',
                                },
                            }}
                        />
                    ) : (
                        <NoRecords message="Select school to view lottery records" useAlert />
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default LotteryFiles;